/**
 * A contact's detail page (F7).
 *
 * The point of a contact record is to reach the person, so the ways of doing
 * that lead — email and phone as working links, above everything else. The
 * organisation and the primary flag sit in the header, because "who is this
 * and do they speak for the startup?" is the question the page is opened with.
 *
 * Read-only, like the customer and asset pages: editing needs a form with an
 * organisation picker backed by the startup and customer directories (task
 * 7.12), and the record is more useful readable now than editable later.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 7.5, PRD §5.7, §5.9
 */
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import { DefinitionList } from '../../components/domain/DefinitionList';
import { Badge } from '../../components/ui/Badge';
import { Card, CardBody, CardHead } from '../../components/ui/Card';
import { Grid, Stack } from '../../components/ui/Grid';
import { Kicker } from '../../components/ui/Kicker';
import { Note } from '../../components/ui/Note';
import { PageHead } from '../../components/ui/PageHead';
import { formatDate } from '../../lib/format';
import { customerPath, ROUTES, startupPath } from '../../lib/routes';

import { useContact } from './api/contacts.queries';

export function ContactDetailPage(): React.JSX.Element {
  const { t } = useTranslation();
  const { id = '' } = useParams<{ id: string }>();

  const { data, isPending, isError } = useContact(id);

  if (isPending) {
    return (
      <p role="status" className="text-body">
        {t('state.loading')}
      </p>
    );
  }

  if (isError) {
    return (
      <div>
        <p role="alert" className="text-sem-bad mb-s2">
          {t('contact.notFound')}
        </p>
        <Link to={ROUTES.contacts} className="text-blue text-sm underline">
          {t('contact.backToList')}
        </Link>
      </div>
    );
  }

  const notSet = <span className="text-gray-400">{t('startup.notSet')}</span>;

  /**
   * The organisation, linked where there is one to link to.
   *
   * INTERNAL, ADVISOR and INVESTOR contacts belong to neither, and naming the
   * type is the correct answer there rather than showing a gap.
   */
  const organisation =
    data.startupId !== null && data.startupName !== null ? (
      <Link to={startupPath(data.startupId)} className="text-blue hover:underline">
        {data.startupName}
      </Link>
    ) : data.customerId !== null && data.customerName !== null ? (
      <Link to={customerPath(data.customerId)} className="text-blue hover:underline">
        {data.customerName}
      </Link>
    ) : (
      t(`contact.orgType.${data.orgType}`)
    );

  return (
    <>
      <nav aria-label={t('startup.breadcrumb')} className="text-body mb-s2 text-xs">
        {/* Persistently underlined, not only on hover: inside a text block a
            link distinguished by colour alone fails WCAG 1.4.1 (PRD §9.7). */}
        <Link to={ROUTES.contacts} className="text-blue underline">
          {t('contact.title')}
        </Link>
        <span className="text-gray-200 mx-1.5">/</span>
        <span>{data.name}</span>
      </nav>

      <PageHead
        title={data.name}
        lead={
          data.role === null
            ? t(`contact.orgType.${data.orgType}`)
            : t('contact.leadWithRole', {
                role: data.role,
                orgType: t(`contact.orgType.${data.orgType}`),
              })
        }
        /* In the actions slot, not inside the heading: a badge rendered within
           an `<h1>` joins its accessible name, so the page would announce
           itself as "Ana Ribeiro Primary contact". */
        actions={data.isPrimary ? <Badge tone="ok">{t('contact.primary')}</Badge> : null}
      />

      <Grid cols="detail">
        <Stack>
          {/* First, because reaching the person is what the record is for. */}
          <Card>
            <CardHead>
              <Kicker as="h2">{t('contact.sections.reach')}</Kicker>
            </CardHead>
            <CardBody>
              <DefinitionList
                items={[
                  {
                    label: t('contact.fields.email'),
                    value:
                      data.email === null ? (
                        notSet
                      ) : (
                        <a
                          href={`mailto:${data.email}`}
                          className="text-blue hover:underline"
                        >
                          {data.email}
                        </a>
                      ),
                  },
                  {
                    label: t('contact.fields.phone'),
                    /* A `tel:` link, which is what makes this page useful on
                       the 375px viewport PRD §9 requires support for. */
                    value:
                      data.phone === null ? (
                        notSet
                      ) : (
                        <a
                          href={`tel:${data.phone.replace(/\s/g, '')}`}
                          className="text-blue hover:underline"
                        >
                          {data.phone}
                        </a>
                      ),
                  },
                  {
                    label: t('contact.fields.linkedIn'),
                    value:
                      data.linkedIn === null ? (
                        notSet
                      ) : (
                        <a
                          href={data.linkedIn}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="text-blue hover:underline"
                        >
                          {t('contact.fields.linkedInLink')}
                        </a>
                      ),
                  },
                ]}
              />
            </CardBody>
          </Card>

          {data.notes === null ? null : (
            <Card>
              <CardHead>
                <Kicker as="h2">{t('contact.sections.notes')}</Kicker>
              </CardHead>
              <CardBody>
                <p className="text-body text-sm whitespace-pre-line">{data.notes}</p>
              </CardBody>
            </Card>
          )}
        </Stack>

        <Stack>
          <Card>
            <CardHead>
              <Kicker as="h2">{t('contact.sections.organisation')}</Kicker>
            </CardHead>
            <CardBody>
              <DefinitionList
                items={[
                  {
                    label: t('contact.fields.organisation'),
                    value: organisation,
                  },
                  {
                    label: t('contact.fields.role'),
                    value: data.role ?? notSet,
                  },
                  {
                    label: t('contact.fields.orgType'),
                    value: t(`contact.orgType.${data.orgType}`),
                  },
                  {
                    label: t('contact.fields.lastContact'),
                    value:
                      data.lastContactDate === null
                        ? t('contact.columns.never')
                        : formatDate(data.lastContactDate),
                  },
                ]}
              />

              {/* Says what the flag MEANS, not just that it is set. A reader
                  who does not know the convention cannot act on a badge. */}
              {data.isPrimary ? (
                <Note className="mt-s3">{t('contact.primaryNote')}</Note>
              ) : null}
            </CardBody>
          </Card>

          <Note>{t('contact.readOnlyNote')}</Note>
        </Stack>
      </Grid>
    </>
  );
}
